import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
    BarChart3,
    KeyRound,
    SlidersHorizontal,
    LogOut,
    Brain,
    Users,
    Activity,
    ShieldCheck,
    Lightbulb,
    Layers,
    DollarSign,
    FlaskConical,
    ChevronDown,
    ChevronRight,
    Sparkles,
    Tag,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAppVersion } from "@/lib/appVersion";
import MongoAlertBanner from "@/components/mongo/MongoAlertBanner";

// QA submenu items — ingestion limited to the SiteCentric superdomain test
// scope. Production ingestion happens via published API flows only.
const QA_CHILDREN = [
    { to: "/qa/protocols", label: "Protocol Ingestion", testId: "nav-qa-protocols" },
    { to: "/qa/ingest-files", label: "Ingest Files", testId: "nav-qa-ingest-files" },
    { to: "/qa/form-generation", label: "Form Generation", testId: "nav-qa-form-generation" },
    { to: "/qa/bd-funnel", label: "BD Funnel Dashboard", testId: "nav-qa-bd-funnel" },
    { to: "/qa/ripeness-radar", label: "Trial Ripeness Radar", testId: "nav-qa-ripeness-radar" },
    { to: "/qa/value-assumptions", label: "Value assumptions", testId: "nav-qa-value-assumptions" },
    { to: "/qa/chat", label: "Chat", testId: "nav-qa-chat" },
    { to: "/qa/ctas", label: "CTA Ingestion", testId: "nav-qa-ctas" },
    { to: "/qa/budgets", label: "Budget Ingestion", testId: "nav-qa-budgets" },
    { to: "/qa/study-builds", label: "Study Build", testId: "nav-qa-study-builds" },
];

const MONITORING_CHILDREN = [
    { to: "/ingest-jobs", label: "Ingest Job Manager", testId: "nav-ingest-jobs" },
    { to: "/tasks", label: "Task Queue", testId: "nav-tasks", requires: "system.tasks.manage" },
    { to: "/webhook-queue", label: "Webhook Queue", testId: "nav-webhook-queue", requires: "observability.view" },
    { to: "/observability", label: "Observability", testId: "nav-observability", requires: "observability.view" },
    { to: "/audit", label: "Audit Trail", testId: "nav-audit", requires: "audit.view" },
    { to: "/build-check", label: "Build Checks", testId: "nav-build-check", superOnly: true },
];

const REGULATORY_CHILDREN = [
    { to: "/tenant-boundary", label: "Tenant Boundary", testId: "nav-tenant-boundary", requires: "admin.view" },
    { to: "/compliance", label: "Compliance", testId: "nav-compliance", requires: "admin.view" },
    { to: "/regions", label: "Regions", testId: "nav-regions", requires: "admin.view" },
];

// Brain — all knowledge stores: datalake (raw IO), pricing/forms memory
// (reusable canonical units), common knowledge (public corpus), vector
// search (embeddings), S3 files (object-storage browser).
const BRAIN_CHILDREN = [
    { to: "/datalake", label: "Data Lake", testId: "nav-datalake", requires: "datalake.view" },
    { to: "/pricing-memory", label: "Pricing Memory", testId: "nav-pricing-memory", requires: "datalake.view" },
    { to: "/memory", label: "Forms Memory", testId: "nav-memory" },
    { to: "/forms", label: "Generated Forms", testId: "nav-generated-forms" },
    { to: "/common-knowledge", label: "Common Knowledge", testId: "nav-common-knowledge" },
    { to: "/rag", label: "Vector Search", testId: "nav-rag", requires: "admin.view" },
    { to: "/s3-files", label: "S3 Files", testId: "nav-s3-files" },
];

// Access & Finance — tenants and money in one place.
const ACCESS_FINANCE_CHILDREN = [
    { to: "/clients", label: "Clients", testId: "nav-clients", requires: "tenant.manage" },
    { to: "/billing", label: "Billing", testId: "nav-billing", requires: "billing.view" },
];

// Capabilities — the platform's primitives that admins build with:
// flows, agents, the connections module, and the API reference docs.
const CAPABILITIES_CHILDREN = [
    { to: "/flows", label: "Flows", testId: "nav-flows", requires: "flow.view" },
    { to: "/agents", label: "AI Agents", testId: "nav-agents", requires: "agent.view" },
    { to: "/ask-database", label: "Ask the database", testId: "nav-ask-database" },
    { to: "/study-builds", label: "Study Builds", testId: "nav-study-builds", requires: "datalake.view" },
    { to: "/connections", label: "Connections", testId: "nav-connections", requires: "connection.view" },
    { to: "/scheduled-tasks", label: "Scheduled Tasks", testId: "nav-scheduled-tasks", requires: "schedule.view" },
    { to: "/scheduling", label: "Patient Scheduling", testId: "nav-scheduling" },
    { to: "/developer-reference", label: "API Reference", testId: "nav-api-reference" },
];

// Feature Requests — internal product intake (user choice 4a).
const FEATURE_REQUEST_CHILDREN = [
    { to: "/feature-requests/submit", label: "Submit Request", testId: "nav-fr-submit" },
    { to: "/feature-requests", label: "All Requests", testId: "nav-fr-list" },
    { to: "/feature-requests/integration-secrets", label: "Integration Secrets", testId: "nav-fr-integration-secrets", requires: "settings.view" },
];

// Each nav entry declares the permission(s) required to see it.
// Anything without `requires` is visible to all authenticated admins.
const NAV = [
    { to: "/", label: "Dashboard", icon: BarChart3, end: true, testId: "nav-dashboard" },
    { group: "access_finance", label: "Access & Finance", icon: DollarSign, testId: "nav-access-finance", children: ACCESS_FINANCE_CHILDREN },
    { group: "capabilities", label: "Capabilities", icon: Layers, testId: "nav-capabilities", children: CAPABILITIES_CHILDREN },
    { group: "feature_requests", label: "Feature Requests", icon: Sparkles, testId: "nav-feature-requests", children: FEATURE_REQUEST_CHILDREN },
    { group: "brain", label: "Brain", icon: Brain, testId: "nav-brain", children: BRAIN_CHILDREN },
    { group: "qa", label: "Quality Assurance", icon: FlaskConical, testId: "nav-qa", requires: "datalake.view", children: QA_CHILDREN },
    { group: "regulatory", label: "Regulatory", icon: ShieldCheck, testId: "nav-regulatory", children: REGULATORY_CHILDREN },
    { group: "monitoring", label: "Monitoring", icon: Activity, testId: "nav-monitoring", children: MONITORING_CHILDREN },
    { to: "/ideas", label: "Idea Register", icon: Lightbulb, testId: "nav-ideas" },
    { to: "/admins", label: "Administrators", icon: Users, testId: "nav-admins", requires: "admin.view" },
    { group: "settings", label: "Settings", icon: SlidersHorizontal, testId: "nav-settings", requires: "settings.view", children: [
        { to: "/settings", label: "General", testId: "nav-settings-general", end: true },
        { to: "/settings/llm-keys", label: "LLM Keys", testId: "nav-settings-llm-keys" },
        { to: "/settings/shared-llm", label: "Shared LLM Providers", testId: "nav-settings-shared-llm" },
        { to: "/settings/radar-sync", label: "Radar Sync", testId: "nav-settings-radar-sync" },
        { to: "/settings/datalake-contexts", label: "Data Lake Contexts", testId: "nav-settings-datalake-contexts" },
        { to: "/settings/sitecentric-agent", label: "SiteCentric Agent (legacy)", testId: "nav-settings-sitecentric-agent" },
        { to: "/settings/mongodb-mcp", label: "MongoDB MCP", testId: "nav-settings-mongodb-mcp" },
    ] },
];

// Routes that belong to each group — used to auto-expand the group when
// the user is already on one of its pages.
const GROUP_ROUTE_PREFIXES = {
    qa: ["/qa/"],
    monitoring: ["/tasks", "/webhook-queue", "/observability", "/audit", "/build-check"],
    regulatory: ["/tenant-boundary", "/compliance", "/regions"],
    settings:   ["/settings"],
    brain: ["/datalake", "/pricing-memory", "/memory", "/forms", "/common-knowledge", "/rag", "/s3-files"],
    access_finance: ["/clients", "/billing"],
    capabilities: ["/flows", "/agents", "/ask-database", "/study-builds", "/connections", "/scheduled-tasks", "/developer-reference"],
    feature_requests: ["/feature-requests"],
};

export default function AppLayout() {
    const { user, logout, hasPermission } = useAuth();
    const location = useLocation();
    const appVersion = useAppVersion();

    // Filter the top-level NAV by permission, then filter group children
    // by permission too (so a partially-empty group disappears entirely).
    const visibleNav = NAV
        .filter((item) => !item.requires || hasPermission(item.requires))
        .map((item) => {
            if (!item.children) return item;
            const visibleChildren = item.children.filter(
                (c) => (!c.requires || hasPermission(c.requires)) && (!c.superOnly || user?.role === "super_admin")
            );
            return { ...item, children: visibleChildren };
        })
        .filter((item) => !item.children || item.children.length > 0);

    // Track per-group expanded state. Auto-expand any group whose route
    // prefix matches the current path.
    const groupOnRoute = (key) => {
        const prefixes = GROUP_ROUTE_PREFIXES[key] || [];
        return prefixes.some((p) =>
            p.endsWith("/")
                ? location.pathname.startsWith(p)
                : (location.pathname === p || location.pathname.startsWith(p + "/"))
        );
    };
    const [openGroups, setOpenGroups] = React.useState(() => {
        const init = {};
        for (const key of Object.keys(GROUP_ROUTE_PREFIXES)) {
            init[key] = groupOnRoute(key);
        }
        return init;
    });
    React.useEffect(() => {
        // Auto-expand the active group on navigation; never auto-collapse
        // a group the user has expanded manually.
        setOpenGroups((prev) => {
            const next = { ...prev };
            for (const key of Object.keys(GROUP_ROUTE_PREFIXES)) {
                if (groupOnRoute(key)) next[key] = true;
            }
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
    const toggleGroup = (key) =>
        setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

    return (
        <div className="min-h-screen flex bg-[#050505] text-white">
            {/* Sidebar */}
            <aside className="w-64 shrink-0 border-r border-white/10 flex flex-col bg-[#0a0a0a]">
                <div className="px-5 pt-6 pb-5 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="grid grid-cols-1 gap-1">
                            <div className="flex gap-0.5">
                                <span className="w-1.5 h-1.5 rounded-sm bg-[#7C3AED]" />
                                <span className="w-1.5 h-1.5 rounded-sm bg-[#7C3AED]" />
                            </div>
                            <div className="flex gap-0.5">
                                <span className="w-1.5 h-1.5 rounded-sm bg-[#7C3AED]" />
                                <span className="w-1.5 h-1.5 rounded-sm bg-[#7C3AED]" />
                            </div>
                        </div>
                        <div>
                            <div className="font-display text-sm font-semibold tracking-tight">
                                <span className="text-white">AI</span>
                                <span className="text-[#7C3AED]">Service</span>
                            </div>
                            <div className="label-xs mt-0.5 text-white/40">
                                CTMS Helper Console
                            </div>
                        </div>
                    </div>
                </div>

                <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                    {visibleNav.map((item) => {
                        const Icon = item.icon;
                        if (item.group) {
                            const expanded = !!openGroups[item.group];
                            const onRoute = groupOnRoute(item.group);
                            const Chevron = expanded ? ChevronDown : ChevronRight;
                            return (
                                <div key={`group-${item.group}`}>
                                    <button
                                        type="button"
                                        onClick={() => toggleGroup(item.group)}
                                        data-testid={item.testId}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                                            onRoute
                                                ? "bg-white/10 text-white"
                                                : "text-white/70 hover:text-white hover:bg-white/5"
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" strokeWidth={1.5} />
                                        <span className="flex-1 text-left">{item.label}</span>
                                        <Chevron className="w-3.5 h-3.5 opacity-60" strokeWidth={1.5} />
                                    </button>
                                    {expanded && (
                                        <div className="ml-7 mt-0.5 mb-1 space-y-0.5 border-l border-white/10 pl-2">
                                            {item.children.map((child) => (
                                                <NavLink
                                                    key={child.to}
                                                    to={child.to}
                                                    end={child.end}
                                                    data-testid={child.testId}
                                                    className={({ isActive }) =>
                                                        `block px-2.5 py-1.5 rounded-md text-xs transition-colors ${
                                                            isActive
                                                                ? "bg-white text-black font-medium"
                                                                : "text-white/60 hover:text-white hover:bg-white/5"
                                                        }`
                                                    }
                                                >
                                                    {child.label}
                                                </NavLink>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        }
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={item.testId}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? "bg-white text-black font-medium"
                                            : "text-white/70 hover:text-white hover:bg-white/5"
                                    }`
                                }
                            >
                                <Icon className="w-4 h-4" strokeWidth={1.5} />
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="p-3 border-t border-white/10">
                    <div className="px-2 pb-3">
                        <div className="label-xs text-white/40">Signed in</div>
                        <div className="text-sm mt-1 truncate" data-testid="admin-email">
                            {user?.email}
                        </div>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="logout-button"
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                    >
                        <LogOut className="w-4 h-4" strokeWidth={1.5} />
                        Sign out
                    </button>
                    <NavLink
                        to="/release-notes"
                        data-testid="sidebar-version-link"
                        title="Release notes"
                        className={({ isActive }) => `mt-1 flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] font-mono transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/40 hover:text-white hover:bg-white/5"}`}
                    >
                        <Tag className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span data-testid="sidebar-version">{appVersion ? `v${appVersion.version}` : "v…"}</span>
                        <span className="ml-auto font-sans">Release notes</span>
                    </NavLink>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0 flex flex-col">
                <header className="h-14 border-b border-white/10 flex items-center px-6 bg-[#050505]">
                    <div className="flex items-center gap-3">
                        <span className="label-xs text-white/40">
                            {location.pathname === "/" ? "Overview" : location.pathname.replace("/", "")}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-white/20" />
                        <span className="text-xs text-white/50 font-mono" data-testid="header-version">
                            {appVersion ? `v${appVersion.version}` : "v…"} · production
                        </span>
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                        <span className="label-xs text-emerald-400/80 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            API Online
                        </span>
                    </div>
                </header>
                <MongoAlertBanner />
                <div className="flex-1 overflow-auto">
                    <div className="max-w-[1600px] mx-auto w-full">
                        <Outlet />
                    </div>
                </div>
            </main>
        </div>
    );
}
